import { withNamespaces } from "react-i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import { withRouter, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { Button, Col, Container, Row } from "reactstrap";
import { EditorState } from "draft-js";
import { slugify } from 'transliteration';
import SuccessModal from "./components/SuccessModal";

import { convertToHTML, convertFromHTML } from "draft-convert";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Spinner from 'react-bootstrap/Spinner';

import {
    confrimPromptUpdateReadyToShowState,
    confirmPromptUpdateContentState,
    confirmPromptResetState,
    confirmPromptHidePrompt
} from 'src/store/actions';

import ThirdpartyUsersService from "src/modules/3rd-party-management/apis/common/ThirdpartyUsersService";
import CommonService from "src/modules/3rd-party-management/apis/common/CommonService";

import Navigator from "src/components/Navigator";
import Tabs from './components/tabs';
import CompanyDetails from "./pages/company-details";
import FactorySitesAndProducts from './pages/factory';
import Contacts from './pages/contacts';
import ReviewReport from './pages/review';
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";
import { useHistory } from "react-router-dom";
import InsufficientLicenseErrorModal from "./components/InsufficientLicenseErrorModal";
import DateUtils from "src/services/utils/DateUtils";

const NewThirdParty = ({ 
    t, 
    user, 
    organization, 
    exitConfirmPromptOptions, 
    updateConfirmPromptReadyToShowState,
    updateConfirmPromptContent,
    resetConfirmPromptState,
    hideConfirmPrompt,
    modules: {
        featuresLoadingStatus   :   currentModuleFeaturesLoadingStatus
    }
}) => {
    const location = useLocation();
    const history = useHistory();
    const tabsActionsRef = useRef();
    const hasEnitityFeature = currentModuleFeaturesLoadingStatus === 'loaded' && isFeatureEnabledForActiveModule('entity_management')

    const [activeTab, setActiveTab] = useState({});

    const [ stepsData, setStepsData ] = useState({
        companyDetails: null,
        factory: null,
        contacts: null
    });
    const [ hasLicence, setHasLicence ] = useState(false);

    const [ selectedLegalDataIsLoading, setSelectedLegalDataIsLoading ] = useState(false);  

    const [ saveAsDraftInProcess, setSaveAsDraftInProcess ] = useState(false);

    const searchParams = new URLSearchParams(location.search);

    const thirdPartyType = searchParams.get('type');

    const isOwnCompany = thirdPartyType === 'own';

    const [openSuccessModal, setOpenSuccessModal] = useState(false);
    const [ dataShouldBeSaved, setDataShouldBeSaved ] = useState(false);
    const [existenseErrorModal, setExistenceErrorModal] = useState(false);

    const handleCheckLicenceExistence = useMutation({
        mutationFn: async () => {
            const service = ThirdpartyUsersService.getInstance();
            return await isOwnCompany ? service.checkOwnCompanyLicenseExistence() : service.checkThirdpartyLicenceExistence();
        },
        onSuccess: () => {
            setHasLicence(true);
        },
        onError: (error) => {
            if(error?.response?.data?.error === "insufficient_license") {
                setHasLicence(false);
                setExistenceErrorModal(true);
            } else {
                toast(t("Checking the existense of licences failed."), {
                    type: "error",
                });
                return history.goBack();
            }

        },
    });

    const handleCreateThirdPartyMutation = useMutation({
        mutationFn: async (payload) => {
            const service = ThirdpartyUsersService.getInstance();

            return await service.create(payload);
        },
        onSuccess: () => {
            removeDataFromStorage();
            resetConfirmPromptState();
            setOpenSuccessModal(true);
        },
        onError: ({ response }) => {
            const {
                data
            } = response || {}

            if (data?.error === 'insufficient_license') {
                toast(t(
                    isOwnCompany ? 
                    "You have reached the maximum number of companies allowed by your license. Creating additional companies is not possible. Please contact support if you need to increase your limit."
                    : 
                    "You have reached the maximum number of third parties allowed by your license. Creating additional third parties is not possible. Please contact support if you need to increase your limit."
                ), {
                    type: "error"
                });
            }
            else if(data?.error === 'miss token'){
                toast(t("Your session has expired or the authentication token is missing. Please reload the page and log in again."), {
                    type: "error"
                });
            }
            else if(data?.message === 'There was a validation error'){
                toast(t("There was a validation error. Please check your input and try again."), {
                    type: "error"
                });
            }
            else {
                toast(t(
                    isOwnCompany ?
                    "An error occurred while creating new company." : 
                    "An error occurred while creating third party."
                ), {
                    type: "error"
                });
            }
        },
    });

    const {
        data        :   legalEntitiesData,
        isFetching  :   legalEntitiesListIsFetching
    } = useQuery({
        queryKey: [
            '3rd-party-management-fetch-legal-entities-list',
            thirdPartyType
        ],
        queryFn: async () => {
            const service = CommonService.getInstance();
            return await isOwnCompany ? service.fetchUnlinkedLegalEntities() : service.fetchLegalEntities();
        },
        enabled: hasEnitityFeature && hasLicence,
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('An error occurred while fetching entities.'), {
                type: 'error',
            });
        }
    });

    const countriesQuery = useQuery({
        queryKey: ['3rd-party-management-fetch-countries-list'],
        queryFn: async () => {
            const service = ThirdpartyUsersService.getInstance();
            return await service.fetchCountries();
        },
        cacheTime: 0,
        enabled: hasLicence,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('An error occurred while fetching products list.'), {
                type: 'error',
            });
        },
    });

    const {
        data        :   selectedLegalData,
        refetch     :   fetchLegalEntityDetailsQuery,
        isFetching  :   legalDetailsQueryIsFetching
    } = useQuery({
        queryKey: ['3rd-party-management-fetch-legal-entity-details', stepsData?.companyDetails?.entities?.value],
        queryFn: async ({
            queryKey
        }) => {
            setSelectedLegalDataIsLoading(true);
            const service = CommonService.getInstance();

            return await service.fetchLegalEntityDetails(queryKey[1]);
        },
        enabled: false,
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('An error occurred while fetching the details of entity.'), {
                type: 'error',
            });
        }
    });

    const memoizedEntitiesList = useMemo(() => {
        return (legalEntitiesData?.legalEntities|| []).map((legal) => {
            return {
                value       :   legal.id,
                label       :   legal.title,
                shortName   :   legal.shortName,
                naceCode    :   legal.nace_code
            }
        }) 
    }, [ legalEntitiesData ]);

    const memoizedCountriesList = useMemo(() => {
        return (countriesQuery.data || []).map((country) => {
            return {
                value: country.id,
                baseLabel: country.name,
                label: t(country.name),
                code: country.code,
            };
        })
    }, [ countriesQuery.data, t ]);

    useEffect(() => {
        if(exitConfirmPromptOptions.show){
            const continueAction = () => {
                exitConfirmPromptOptions.callbackFunc && exitConfirmPromptOptions.callbackFunc(true);
                resetConfirmPromptState();
            }

            switch (exitConfirmPromptOptions.confirmStatus) {
                case 'saveAndExit':
                    saveDataOnTheStorage()
                        .then(() => continueAction())
                        .catch(() => {
                            toast(t("An error occurred while saving data."), {
                                type: "error",
                            });

                            continueAction();
                        });
                break;

                case 'discard':
                    removeDataFromStorage();

                    setTimeout(() => {
                        continueAction();
                    }, 250);
                break;

                case 'continue':
                    hideConfirmPrompt();
                break;

                default: break;
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ 
        exitConfirmPromptOptions.confirmStatus,
        exitConfirmPromptOptions.show,
        t
    ]);

    useEffect(() => {
        updateConfirmPromptContent({
            title: (
                <div className="d-flex justify-content-start align-items-center">
                    {t('As you are leaving the page')}
                </div>
            ),
            message: (
                <>
                    <p className="mb-2 fw-semibold">
                        {t('You have unsaved changes.')}
                    </p>
                    <p className="text-muted mb-0">
                        {t('Do you want to save the current state to continue later?')}
                    </p>
                </>
            ),
            buttons: ({ updateConfirmPromptConfirmStatus }) => {
                return (
                    <>
                        <Button
                            color='secondary'
                            onClick={() => updateConfirmPromptConfirmStatus('continue')}
                            disabled={saveAsDraftInProcess}
                        >
                            {t('Continue Editing')}
                        </Button>

                        <Button
                            color='primary'
                            onClick={() => updateConfirmPromptConfirmStatus('saveAndExit')}
                            disabled={saveAsDraftInProcess}
                        >
                            {saveAsDraftInProcess && (
                                <Spinner 
                                    size="sm" 
                                    color="white" 
                                    className="me-1"
                                />
                            )}
                            {t('Save as Draft & Exit')}
                        </Button>

                        <Button
                            color='danger'
                            onClick={() => updateConfirmPromptConfirmStatus('discard')}
                            disabled={saveAsDraftInProcess}
                        >
                            {t('Discard Changes')}
                        </Button>
                    </>
                )
            }
        })
    }, [
        t,
        saveAsDraftInProcess,
        updateConfirmPromptContent
    ]);

    useEffect(() => {
        updateConfirmPromptReadyToShowState(dataShouldBeSaved)
    }, [
        dataShouldBeSaved,
        updateConfirmPromptReadyToShowState
    ]);

    useEffect(() => {
        handleCheckLicenceExistence.mutate();

        const prevData = localStorage.getItem('MODULE_TPDD_NEW_THIRDPARTY_SAVED_DATA');

        if(prevData){
            try{
                const parsedData = JSON.parse(prevData);

                if(parsedData){
                    setStepsData({
                        companyDetails: parsedData.companyDetails ? {
                            ...parsedData.companyDetails,
                            description: EditorState.createWithContent(convertFromHTML(parsedData.companyDetails.description || ''))
                        } : {},
                        factory: parsedData.factory || {}
                    });
                }
            }
            catch(error){
                removeDataFromStorage();
            }
        }
    }, []);

    useEffect(() => {
        const selectedLegalId = stepsData?.companyDetails?.entities?.value;

        if(isOwnCompany && selectedLegalId){
            fetchLegalEntityDetailsQuery();
        }
    }, [ stepsData.companyDetails?.entities?.value ]);

    useEffect(() => {
        const gotLocations = selectedLegalData?.legal_entity_locations || [];
        if(memoizedCountriesList.length > 0 && selectedLegalDataIsLoading){
            setStepsData((currenState) => {
                return {
                    ...currenState,
                    factory: {
                        ...currenState.factory,
                        office_addresses: gotLocations.map((address) => {
                            return {
                                country: memoizedCountriesList.find((country) => country.value === address.country.id),
                                city: address.city,
                                postalCode: address.postal_code,
                                extra: address.address
                            }
                        })
                    }
                }
            });

            if(!gotLocations.length){
                setSelectedLegalDataIsLoading(false);
            }
        }
    }, [ selectedLegalData, memoizedCountriesList ]);

    useEffect(() => {
        if(isOwnCompany && selectedLegalDataIsLoading){
            setSelectedLegalDataIsLoading(false);
        }
    }, [ stepsData.factory?.office_addresses ]);
    
    const handleCreateThirdparty = () => {
        const dateUtils = new DateUtils();

        const {
            companyDetails,
            factory,
            contacts
        } = stepsData;

        const languages = organization.languages.map(l => {
            return {
                name: l.name,
                direction: l.direction,
                language: l.language?.id,
                isDefault: l.is_default,
                module: l.module?.id
            }
        })

        const products = (factory.products || []).filter((product) => product.product?.value).map((product) => {
            return {
                name: product.product.value,
                description: product.description,
            };
        });

        const supplierLocations = [];

        (factory.office_addresses || []).forEach((address) => {
            supplierLocations.push({
                type: "office",
                country: address.country.value,
                city: address.city,
                postalCode: address.postalCode, 
                address: address.extra,
            });
        });

        (factory.factory_addresses || []).forEach((address) => {
            if(address.country){
                supplierLocations.push({
                    type: "manufacture",
                    country: address.country.value,
                    city: address.city,
                    postalCode: address.postalCode, 
                    address: address.extra,
                });
            }
        });

        const data = {
            type: !isOwnCompany ? thirdPartyType : undefined,
            subType: !isOwnCompany ? companyDetails.subType : undefined,
            customSubType: !isOwnCompany && companyDetails.subType === 'other' ? companyDetails.customSubType : undefined,
            isOwnCompany: isOwnCompany ? true : undefined,
            token: localStorage.getItem("LOCAL_STORAGE_KEY_ACCESS_TOKEN"),
            name: companyDetails.company_name,
            shortName: !isOwnCompany ? companyDetails.company_shortname : undefined,
            domainName: slugify(companyDetails.company_name),
            languages: languages,
            products: products,
            supplierLocations: supplierLocations,
            industries: (companyDetails.industries || []).map(industry => String(industry?.value)),
            companySize: companyDetails.company_size,
            vatNumber: companyDetails.vat_number,
            websiteUrl: companyDetails.website,
            brands: companyDetails.brands,
            description: companyDetails.description.getCurrentContent().hasText()
                ? convertToHTML(companyDetails.description.getCurrentContent())
                : "",
            isPartner: 0,
            partnerAnalysts: [String(user?.id)],
            partner: Number(organization?.customerId),
            hasDashboard: 0,
            subscriptionType: ""
        };

        if (thirdPartyType === 'individual') {
            data.individualDateOfBirth = dateUtils.convertDateToDate(
                companyDetails.individual_date_of_birth, 'YYYY-MM-DD'
            );

            if (companyDetails.individual_birth_place_country?.value) {
                data.individualBirthPlaceCountry = companyDetails.individual_birth_place_country.value;
            }

            data.individualTaxId = companyDetails.individual_tax_id;
            data.individualNationalIdNumber = companyDetails.individual_national_id_number;
        }

        if(isOwnCompany){
            data.responsiblePersons = contacts.map((p) => {
                return p.id.toString()
            })

            data.legalEntities = [companyDetails.entities.value.toString()]

            data.countryCode = factory.office_addresses[0]?.country?.code
        }
        else{
            data.contactPersons = contacts?.map((c) => {
                return {
                    title       : c.title,
                    name        : c.firstName,
                    lastName    : c.lastName,
                    email       : c.email,
                    phoneNumber : c.phoneNumber,
                    position    : c.position,
                    language    : c.language?.value,
                };
            });

            data.legalEntities = (companyDetails.entities || []).map((legal) => {
                return legal.value.toString()
            })

            
            data.countryCode = factory.office_addresses[0]?.country?.code
        }

        handleCreateThirdPartyMutation.mutate(data)
    }

    const saveDataOnTheStorage = () => {
        return new Promise((res, rej) => {
            setSaveAsDraftInProcess(true);

            try{
                const description = stepsData.companyDetails.description
        
                localStorage.setItem('MODULE_TPDD_NEW_THIRDPARTY_SAVED_DATA', JSON.stringify({
                    ...stepsData,
                    contacts: undefined,
                    companyDetails: {
                        ...stepsData.companyDetails,
                        description: description.getCurrentContent().hasText() ? 
                            convertToHTML(description.getCurrentContent()) : 
                            ""
                    }
                }));

                res();
            }
            catch(error){
                rej();
            }

            setSaveAsDraftInProcess(false);
        });
    }

    const removeDataFromStorage = () => {
        localStorage.removeItem('MODULE_TPDD_NEW_THIRDPARTY_SAVED_DATA');
    }
    
    return (
        <div className="page-content new-thirdparty-page">
            {openSuccessModal && <SuccessModal supplierType={thirdPartyType} setModal={setOpenSuccessModal} modal={openSuccessModal} />}
            {existenseErrorModal && <InsufficientLicenseErrorModal supplierType={thirdPartyType} setModal={setExistenceErrorModal} modal={existenseErrorModal} />}

            <Container fluid>
                <Row>
                    <Col lg="12" className="mb-4">
                        <Navigator
                            pageTitle={t(isOwnCompany ? 'New Own Company' : 'New Third Party')}
                            translate={false}
                        />
                    </Col>
                </Row>

                <Row>
                    <Col sm='12'>
                        <Tabs 
                            thirdPartyType={thirdPartyType}
                            actionsRef={tabsActionsRef}
                            onActiveTabChanged={(tab) => setActiveTab(tab)}
                        />
                    </Col>
                </Row>

                {handleCheckLicenceExistence.isLoading ? (
                    <div className="d-flex justify-content-center align-items-center">
                        <Spinner size='sm' className="me-1" color="primary" />
                        {t('Please wait')}...
                    </div>
                ) : (
                    <>
                        {activeTab.name === 'company-details' && (
                            <CompanyDetails
                                setCompanyDetailsTabCompleted={(companyDetailsData) => {
                                    setStepsData({
                                        ...stepsData,
                                        companyDetails: companyDetailsData
                                    });

                                    if(tabsActionsRef.current){
                                        tabsActionsRef.current.goToNext()
                                    }
                                    
                                    setDataShouldBeSaved(true);
                                }}
                                thirdPartyType={thirdPartyType}
                                memoizedEntitiesList={memoizedEntitiesList}
                                defaultValues={stepsData?.companyDetails}
                                memoizedCountriesList={memoizedCountriesList}
                                isLoading={legalEntitiesListIsFetching || countriesQuery.isFetching}
                            />
                        )}

                        {activeTab.name === 'location-and-products' && (
                            <FactorySitesAndProducts
                                selectedIndustryIds={ 
                                    (stepsData.companyDetails?.industries || []).map((industry) => industry.value) 
                                }
                                setCompanyDetailsTabCompleted={(factoryData) => {
                                    setStepsData({
                                        ...stepsData,
                                        factory: factoryData
                                    });

                                    if(tabsActionsRef.current){
                                        tabsActionsRef.current.goToNext()
                                    }
                                }}
                                goToPrevTab={(factoryCurrentState) => {
                                    setStepsData({
                                        ...stepsData,
                                        factory: factoryCurrentState
                                    });

                                    if(tabsActionsRef.current){
                                        tabsActionsRef.current.goToPrev()
                                    }
                                }}
                                thirdPartyType={thirdPartyType}
                                defaultValues={stepsData?.factory}
                                memoizedCountriesList={memoizedCountriesList}
                                isLoading={countriesQuery.isFetching || legalDetailsQueryIsFetching || selectedLegalDataIsLoading}
                            />
                        )}

                        {activeTab.name === "contact-person" && (
                            <Contacts
                                setCompanyDetailsTabCompleted={(contacts) => {
                                    setStepsData({
                                        ...stepsData,
                                        contacts: contacts
                                    });

                                    if(tabsActionsRef.current){
                                        tabsActionsRef.current.goToNext()
                                    }
                                }}
                                goToPrevTab={(contactsCurrentState) => {
                                    setStepsData({
                                        ...stepsData,
                                        contacts: contactsCurrentState
                                    });

                                    if(tabsActionsRef.current){
                                        tabsActionsRef.current.goToPrev()
                                    }
                                }}
                                thirdPartyType={thirdPartyType}
                                defaultValues={stepsData?.contacts}
                            />
                        )}

                        {activeTab.name === "finalize" && (
                            <ReviewReport
                                goToSpecificTab={(payload) => {
                                    if(tabsActionsRef.current){
                                        tabsActionsRef.current.goToSpecificTab(payload)
                                    }
                                }}
                                goToPrevTab={() => {
                                    if(tabsActionsRef.current){
                                        tabsActionsRef.current.goToPrev()
                                    }
                                }}
                                thirdPartyType={thirdPartyType}
                                values={stepsData}
                                onSubmit={handleCreateThirdparty}
                                isLoading={handleCreateThirdPartyMutation.isLoading}
                            />
                        )}
                    </>
                )}
            </Container>
        </div>
    );
};

const mapStatetoProps = (state) => {
    const { token } = state.Login;
    const { Organization } = state;
    return {
        user: state.Login.user,
        token,
        organization: Organization,
        exitConfirmPromptOptions : state.ExitConfirmPrompt,
        modules: state.Modules
    };
};

export default withNamespaces()(
    withRouter(connect(mapStatetoProps, {
        updateConfirmPromptReadyToShowState: (payload) => confrimPromptUpdateReadyToShowState(payload),
        updateConfirmPromptContent: (payload) => confirmPromptUpdateContentState(payload),
        resetConfirmPromptState: () => confirmPromptResetState(),
        hideConfirmPrompt: () => confirmPromptHidePrompt()
    })(NewThirdParty))
);
